import { Model } from 'app/model/backendmodel';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { ActivityAsyncAdapter } from './activity.adapter';
import { Metamodel } from 'app/model/base/model';
import _ from 'lodash';
import { ReferenceAdapter } from '../reference.adapter';
import { SparkJobDefinition, BigDataPool, SynapseSparkJobDefinitionActivity, AzureSynapseArtifactsLinkedService, A365BackendModel, SparkConfiguration, Credential } from 'shell-models-lib';
import { toArgsArray, toArgsString } from 'app/model/adapters/activities/arguments-adapter.util';
import { ValueAdapter } from '../value.adapter';
import { toParameterClass, toParameterObject } from 'app/model/adapters/linkedservices/parameter-config.util';
import { convertParametersToModel } from 'app/shared/utils/parameter-converter.util';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(SynapseSparkJobDefinitionActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        description: '*',
        type: '*',
        linkedServiceName: '*',
        policy: '*',
        dependsOn: '*',
        userProperties: '*',
        typeProperties: {
            sparkJob: '*',
            file: '*',
            className: '*',
            args: '*',
            scanFolder: '*',
            files: '*',
            pythonCodeReference: '*',
            filesV2: '*',
            targetBigDataPool: '*',
            executorSize: '*',
            conf: '*',
            driverSize: '*',
            numExecutors: '*',
            authentication: '*',
            configurationType: '*',
            targetSparkConfiguration: '*',
            sparkConfig: '*',
        }
    }
})
export class SynapseSparkJobDefinitionActivityAdapter extends ActivityAsyncAdapter<SynapseSparkJobDefinitionActivity, A365BackendModel.SynapseSparkJobDefinitionActivity> {

    protected async _toResource(activity: SynapseSparkJobDefinitionActivity): Promise<A365BackendModel.SynapseSparkJobDefinitionActivity> {
        const modelSparkJobDefinitionActivity = super._getCommonActivityProperties(
            activity, Metamodel.modelFor(SynapseSparkJobDefinitionActivity).name) as A365BackendModel.SynapseSparkJobDefinitionActivity;
        modelSparkJobDefinitionActivity.typeProperties = {
            sparkJob: this._getJobDefinitionReference(activity),
            file: ValueAdapter.toResource(activity, SynapseSparkJobDefinitionActivity.MAIN_DEFINITION_FILE),
            className: ValueAdapter.toResource(activity, SynapseSparkJobDefinitionActivity.MAIN_CLASS_NAME),
            args: toArgsArray(activity.sparkJobDefinitionArguments, activity, SynapseSparkJobDefinitionActivity.ARGUMENTS),
            scanFolder: ValueAdapter.toResource(activity, SynapseSparkJobDefinitionActivity.SCAN_FOLDER),
            pythonCodeReference: toArgsArray(activity.pythonCodeReference, activity, SynapseSparkJobDefinitionActivity.PYTHON_CODE_REFERENCE),
            filesV2: toArgsArray(activity.referenceFilesV2, activity, SynapseSparkJobDefinitionActivity.REFERENCE_FILES_V2),
            targetBigDataPool: this._getSparkpool(activity),
            executorSize: ValueAdapter.toResource(activity, SynapseSparkJobDefinitionActivity.EXECUTOR_SIZE),
            conf: {
                'spark.dynamicAllocation.enabled': ValueAdapter.toResource(activity, SynapseSparkJobDefinitionActivity.DYNAMIC_EXECUTOR),
                'spark.dynamicAllocation.minExecutors': ValueAdapter.toResource(activity, SynapseSparkJobDefinitionActivity.MIN_EXECUTORS),
                'spark.dynamicAllocation.maxExecutors': activity.dynamicExecutor || activity.dynamicExecutor === undefined ?
                                                        ValueAdapter.toResource(activity, SynapseSparkJobDefinitionActivity.MAX_EXECUTORS) :
                                                        ValueAdapter.toResource(activity, SynapseSparkJobDefinitionActivity.MIN_EXECUTORS)
            },
            driverSize: ValueAdapter.toResource(activity, SynapseSparkJobDefinitionActivity.DRIVER_SIZE),
            numExecutors: ValueAdapter.toResource(activity, SynapseSparkJobDefinitionActivity.MIN_EXECUTORS)
        };

        if (activity.authentication?.type) {
            modelSparkJobDefinitionActivity.typeProperties.authentication = {};
            modelSparkJobDefinitionActivity.typeProperties.authentication.type = activity.authentication.type;
            if (activity.authentication.type === 'UserAssignedManagedIdentity' && activity.authentication.credential){
                modelSparkJobDefinitionActivity.typeProperties.authentication.credential = ReferenceAdapter.toResource(activity.authentication.credential, this.context) as Model.CredentialReference
            }
        }

        modelSparkJobDefinitionActivity.typeProperties.configurationType = activity.configurationType;
        modelSparkJobDefinitionActivity.typeProperties.targetSparkConfiguration = this._getSparkConfiguration(activity);
        modelSparkJobDefinitionActivity.typeProperties.sparkConfig = toParameterObject(activity.sparkConfig);

        if (activity.linkedService) {
            modelSparkJobDefinitionActivity.linkedServiceName = this._toLinkedServiceReferenceModel(activity.linkedService, activity.linkedServiceParameters);
        }

        if (activity.raw) {
            const parsed = JSON.parse(activity.raw);
            _.merge(modelSparkJobDefinitionActivity, parsed);
        }

        return modelSparkJobDefinitionActivity;
    }

    protected async _toModel(modelActivity: A365BackendModel.SynapseSparkJobDefinitionActivity): Promise<SynapseSparkJobDefinitionActivity> {
        const activity: SynapseSparkJobDefinitionActivity = SynapseSparkJobDefinitionActivity.MODEL.create<SynapseSparkJobDefinitionActivity>(modelActivity.name, modelActivity.description);
        this._updatedCommonProperties(modelActivity, activity);

        const sparkJobDefinition = modelActivity.typeProperties.sparkJob;
        if (typeof sparkJobDefinition.referenceName === 'string') {
            activity.sparkJobDefinition = ReferenceAdapter.toProxyModel<SparkJobDefinition>(
                Metamodel.modelFor(SparkJobDefinition),
                sparkJobDefinition, this.context);
        } else {
            ValueAdapter.toModel(activity, SynapseSparkJobDefinitionActivity.NAME, sparkJobDefinition.referenceName);
        }

        const args = toArgsString(activity, modelActivity.typeProperties.args, SynapseSparkJobDefinitionActivity.ARGUMENTS);
        if (args) {
            activity.sparkJobDefinitionArguments = args;
        }

        // Migrate to reference files v2
        const files = toArgsString(activity, modelActivity.typeProperties.files, SynapseSparkJobDefinitionActivity.REFERENCE_FILES);
        if (files) {
            activity.pythonCodeReference = files.filter((file) => (file.value?.endsWith('.py') || file.value?.endsWith('.py3')));
            activity.referenceFilesV2 = files.filter((file) => !(file.value?.endsWith('.py') || file.value?.endsWith('.py3')));
            // To detect if the acitvity is the old format
            activity.referenceFiles = files;
        } else {
            toArgsString(activity, modelActivity.typeProperties.files, SynapseSparkJobDefinitionActivity.REFERENCE_FILES_V2);
        }

        const pythonCodeReference = toArgsString(activity, modelActivity.typeProperties.pythonCodeReference, SynapseSparkJobDefinitionActivity.PYTHON_CODE_REFERENCE);
        if (pythonCodeReference) {
            activity.pythonCodeReference = pythonCodeReference;
        }

        const filesV2 = toArgsString(activity, modelActivity.typeProperties.filesV2, SynapseSparkJobDefinitionActivity.REFERENCE_FILES_V2);
        if (filesV2) {
            activity.referenceFilesV2 = filesV2;
        }

        const sparkPool = modelActivity.typeProperties && modelActivity.typeProperties.targetBigDataPool;
        if (sparkPool != null) {
            if (typeof sparkPool.referenceName === 'string') {
                activity.sparkPool = ReferenceAdapter.toProxyModel<BigDataPool>(
                    Metamodel.modelFor(BigDataPool),
                    sparkPool, this.context);
            } else {
                ValueAdapter.toModel(activity, SynapseSparkJobDefinitionActivity.SPARKPOOL, sparkPool.referenceName);
            }
        }

        if (modelActivity.typeProperties) {
            ValueAdapter.toModel(activity, SynapseSparkJobDefinitionActivity.MAIN_DEFINITION_FILE, modelActivity.typeProperties.file);
            ValueAdapter.toModel(activity, SynapseSparkJobDefinitionActivity.MAIN_CLASS_NAME, modelActivity.typeProperties.className);
            ValueAdapter.toModel(activity, SynapseSparkJobDefinitionActivity.SCAN_FOLDER, modelActivity.typeProperties.scanFolder);
            ValueAdapter.toModel(activity, SynapseSparkJobDefinitionActivity.EXECUTOR_SIZE, modelActivity.typeProperties.executorSize);
            ValueAdapter.toModel(activity, SynapseSparkJobDefinitionActivity.DRIVER_SIZE, modelActivity.typeProperties.driverSize);
            ValueAdapter.toModel(activity, SynapseSparkJobDefinitionActivity.DYNAMIC_EXECUTOR, modelActivity.typeProperties.conf ?
                                 modelActivity.typeProperties.conf['spark.dynamicAllocation.enabled'] : null);
            ValueAdapter.toModel(activity, SynapseSparkJobDefinitionActivity.MIN_EXECUTORS, modelActivity.typeProperties.conf ?
                                 modelActivity.typeProperties.conf['spark.dynamicAllocation.minExecutors'] : null);
            ValueAdapter.toModel(activity, SynapseSparkJobDefinitionActivity.MAX_EXECUTORS, modelActivity.typeProperties.conf ?
                                 modelActivity.typeProperties.conf['spark.dynamicAllocation.maxExecutors'] : null);
            activity.configurationType = modelActivity.typeProperties.configurationType as A365BackendModel.ConfigurationType;
            activity.sparkConfig = toParameterClass(modelActivity.typeProperties.sparkConfig, activity);

            if (modelActivity.typeProperties.authentication) {
                activity.authentication.type = modelActivity.typeProperties.authentication.type;
                if (modelActivity.typeProperties.authentication.credential) {
                    activity.authentication.credential = ReferenceAdapter.toProxyModel(
                        Metamodel.modelFor(Credential),
                        modelActivity.typeProperties.authentication.credential,
                        this.context
                    );
                }
            }

            const sparkConfig = modelActivity.typeProperties.targetSparkConfiguration;
            if (sparkConfig != null) {
                if (typeof sparkConfig.referenceName === 'string') {
                    activity.targetSparkConfiguration = ReferenceAdapter.toProxyModel<SparkConfiguration>(
                        Metamodel.modelFor(SparkConfiguration),
                        sparkConfig, this.context);
                } else {
                    ValueAdapter.toModel(activity, SynapseSparkJobDefinitionActivity.TARGET_SPARK_CONFIGURATION, sparkConfig.referenceName);
                }
            }
        }

        if (modelActivity.linkedServiceName != null) {
            activity.linkedService = ReferenceAdapter.toProxyModel<AzureSynapseArtifactsLinkedService>(Metamodel.modelFor(AzureSynapseArtifactsLinkedService),
                modelActivity.linkedServiceName, this.context);
            if (modelActivity.linkedServiceName.parameters) {
                // Parameter value for linked service
                convertParametersToModel(activity,
                    !!this.context.adapterConfig.replaceIdentitiesForNonConcepts,
                    activity.linkedService,
                    activity.linkedServiceParameters,
                    modelActivity.linkedServiceName.parameters);
            }
        }

        return activity;
    }

    private _getJobDefinitionReference(activity: SynapseSparkJobDefinitionActivity): Model.ArtifactReference {
        const sparkJobDefinition = activity.sparkJobDefinition;
        if (!sparkJobDefinition) {
            const name = ValueAdapter.toResource(activity, SynapseSparkJobDefinitionActivity.NAME);
            return name ? {
                referenceName: name,
                type: 'SparkJobDefinitionReference'
            } : undefined;
        }

        if (activity.linkedService) {
            return {
                referenceName: this._toResourceWithLinkedService(sparkJobDefinition.name),
                type: 'SparkJobDefinitionReference'
            };
        }

        return ReferenceAdapter.toResource(sparkJobDefinition, this.context) as Model.ArtifactReference;
    }

    private _getSparkpool(activity: SynapseSparkJobDefinitionActivity): Model.ArtifactReference {
        const sparkPool = activity.sparkPool;
        if (!sparkPool) {
            const name = ValueAdapter.toResource(activity, SynapseSparkJobDefinitionActivity.SPARKPOOL);
            return name ? {
                referenceName: name,
                type: 'bigDataPoolReference'
            } : undefined;
        }

        if (activity.linkedService) {
            return {
                referenceName: this._toResourceWithLinkedService(sparkPool.name),
                type: 'BigDataPoolReference'
            };
        }

        return ReferenceAdapter.toResource(sparkPool, this.context) as Model.ArtifactReference;
    }

    private _getSparkConfiguration(activity: SynapseSparkJobDefinitionActivity): Model.ArtifactReference {
        const sparkConfig = activity.targetSparkConfiguration;
        if (!sparkConfig) {
            const name = ValueAdapter.toResource(activity, SynapseSparkJobDefinitionActivity.TARGET_SPARK_CONFIGURATION);
            return name ? {
                referenceName: name,
                type: 'SparkConfigurationReference'
            } : undefined;
        }

        if (activity.linkedService) {
            return {
                referenceName: this._toResourceWithLinkedService(sparkConfig.name),
                type: 'SparkConfigurationReference'
            };
        }

        return ReferenceAdapter.toResource(sparkConfig, this.context) as Model.ArtifactReference;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private _toResourceWithLinkedService(name: string): any {
        return {
            value : name,
            type: 'Expression'
        };
    }
}
